import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { CurrencyCode } from '@/lib/currency';

export interface CartLine {
  partId: string;
  qty: number;
  unitPrice: number;     // USD whole units
  shippingWeight: number; // lbs
  // Snapshot of part info at add-time so the cart survives part
  // edits/deletes without losing context. Refreshed when re-added.
  snapshot: {
    title: string;
    sku: string;
    casting?: string;
    iconId: string;
  };
}

interface CartState {
  lines: CartLine[];
  currency: CurrencyCode;
  addLine: (line: CartLine) => void;
  removeLine: (partId: string) => void;
  updateLine: (partId: string, patch: Partial<Omit<CartLine, 'partId' | 'snapshot'>>) => void;
  clear: () => void;
  setCurrency: (c: CurrencyCode) => void;
}

export const useCartStore = create<CartState>()(
  persist(
    (set) => ({
      lines: [],
      currency: 'USD',
      addLine: (line) =>
        set((state) => {
          const existing = state.lines.find((l) => l.partId === line.partId);
          if (existing) {
            return {
              lines: state.lines.map((l) =>
                l.partId === line.partId ? { ...l, qty: l.qty + line.qty } : l
              ),
            };
          }
          return { lines: [...state.lines, line] };
        }),
      removeLine: (partId) =>
        set((state) => ({ lines: state.lines.filter((l) => l.partId !== partId) })),
      updateLine: (partId, patch) =>
        set((state) => ({
          lines: state.lines.map((l) =>
            l.partId === partId ? { ...l, ...patch } : l
          ),
        })),
      clear: () => set({ lines: [] }),
      setCurrency: (currency) => set({ currency }),
    }),
    {
      name: 'swc-cart-v1',
      partialize: (state) => ({ lines: state.lines, currency: state.currency }),
    }
  )
);
